import { CadastroRapidoDeDepartamento, CadastroRapidoDeColaborador } from '@/components/cadastro-rapido';
import {
  AccessRuleEffect,
  AccessRuleScope,
  ALL_DOCUMENT_PERMISSION_FLAGS,
  DocumentPermissionFlag,
  type AccessRuleInput,
} from '@qualicloud/contracts';
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Combobox,
  EmptyState,
  Field,
  IconAlertTriangle,
  IconPlus,
  IconTrash,
  IconUsers,
  RadioGroup,
  Select,
  Tooltip,
  cn,
} from '@/design-system';
import {
  ACCESS_EFFECT_LABEL,
  ACCESS_SCOPE_LABEL,
  PERMISSION_FLAG_DESCRIPTION,
  PERMISSION_FLAG_LABEL,
} from '@/lib/i18n';

export interface AccessRuleEditorProps {
  readonly rules: readonly AccessRuleInput[];
  readonly onChange: (rules: AccessRuleInput[]) => void;
  /**
   * A DISTRIBUIÇÃO CONTROLADA, agora derivada das regras.
   *
   * Antes era uma seção própria, com seus próprios campos de colaborador e
   * departamento — e a tela precisava avisar quando alguém era escolhido para
   * dar ciência de um documento que não conseguia abrir. Essa incoerência
   * deixou de ser possível: a exigência de ciência passou a ser um check DA
   * REGRA que concede a leitura, então quem precisa confirmar é sempre,
   * necessariamente, quem pode ver.
   *
   * O check não entra no contrato da regra: ele escreve nestes dois arrays,
   * que são os mesmos que o servidor já recebia. O estado do check é DERIVADO
   * deles — a regra "exige ciência" quando o alvo dela está na lista.
   */
  readonly distributionUserIds: readonly string[];
  readonly distributionDepartmentIds: readonly string[];
  readonly onDistributionChange: (userIds: string[], departmentIds: string[]) => void;
  readonly users: ReadonlyArray<{ readonly id: string; readonly name: string; readonly hint?: string }>;
  readonly departments: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly disabled?: boolean;
  readonly error?: string;
}

/**
 * Editor de regras de ACL nos três escopos.
 *
 * O QUE MUDA EM RELAÇÃO AO LEGADO (ged.md §5.1 e §5.6)
 *  · o escopo era codificado por SENTINELAS em dois combos: departamento "Todos"
 *    + colaborador "Todos" = empresa; departamento X + colaborador "Todos" =
 *    departamento. Ninguém entendia sem ler o código. Aqui o escopo é um campo
 *    explícito com três opções nomeadas;
 *  · não havia negação: uma linha com `reader = 0` não revogava nada, porque a
 *    agregação era `MAX()`. Aqui existe DENY, e ele é destacado visualmente;
 *  · as 6 permissões eram pares de imagens de 25px sobrepostas, alternadas por
 *    clique na célula, com o checkbox real em `display:none` — inacessível por
 *    teclado e invisível para leitor de tela. Aqui são checkboxes reais, com
 *    rótulo, descrição e navegação por Tab;
 *  · a 4ª permissão chamava-se "Distribuição" no rótulo mas gravava a coluna
 *    `security`. Aqui o nome diz o que faz: "Administrar distribuição".
 *
 * O efeito combinado das regras é mostrado pela `<PermissionMatrix>` logo
 * abaixo — o legado não tinha nada equivalente.
 */
export function AccessRuleEditor({
  rules,
  onChange,
  distributionUserIds,
  distributionDepartmentIds,
  onDistributionChange,
  users,
  departments,
  disabled = false,
  error,
}: AccessRuleEditorProps) {
  const [draft, setDraft] = useState<DraftRule>(() => emptyDraft());
  const [draftExigeCiencia, setDraftExigeCiencia] = useState(false);

  const userItems = useMemo(
    () => users.map((user) => ({ value: user.id, label: user.name, hint: user.hint })),
    [users],
  );
  const departmentItems = useMemo(
    () => departments.map((dept) => ({ value: dept.id, label: dept.name })),
    [departments],
  );

  const draftIsValid =
    draft.flags.length > 0 &&
    (draft.scope === AccessRuleScope.ORGANIZATION ||
      (draft.scope === AccessRuleScope.USER && draft.userId !== null) ||
      (draft.scope === AccessRuleScope.DEPARTMENT && draft.departmentId !== null));

  /**
   * Só regra que CONCEDE LEITURA pode exigir ciência.
   *
   * Uma negação não distribui nada, e uma regra sem Visualizar entregaria a
   * pendência a quem não abre o documento — a incoerência que a seção separada
   * de distribuição precisava denunciar num aviso.
   *
   * O escopo ORGANIZAÇÃO fica de fora por uma razão de persistência, não de
   * desenho: `document_distribution` só tem linha de USER e de DEPARTMENT.
   * Aceitar o check aqui gravaria uma exigência que não vira destinatário
   * nenhum — falha silenciosa, que é exatamente o que este produto não pode
   * ter. Para alcançar a empresa inteira, uma regra por departamento.
   */
  const podeExigirCiencia = (rule: {
    readonly scope: string;
    readonly effect: string;
    readonly flags: readonly DocumentPermissionFlag[];
  }): boolean =>
    rule.effect === AccessRuleEffect.ALLOW &&
    rule.flags.includes(DocumentPermissionFlag.READ) &&
    rule.scope !== AccessRuleScope.ORGANIZATION;

  const alvoDaRegra = (rule: AccessRuleInput): string | null =>
    rule.scope === AccessRuleScope.USER
      ? (rule.userId ?? null)
      : rule.scope === AccessRuleScope.DEPARTMENT
        ? (rule.departmentId ?? null)
        : null;

  const exigeCiencia = (rule: AccessRuleInput): boolean => {
    const alvo = alvoDaRegra(rule);
    if (!alvo) return false;
    return rule.scope === AccessRuleScope.USER
      ? distributionUserIds.includes(alvo)
      : distributionDepartmentIds.includes(alvo);
  };

  const definirCiencia = (rule: AccessRuleInput, exigir: boolean) => {
    const alvo = alvoDaRegra(rule);
    if (!alvo) return;
    if (rule.scope === AccessRuleScope.USER) {
      onDistributionChange(
        exigir
          ? [...new Set([...distributionUserIds, alvo])]
          : distributionUserIds.filter((id) => id !== alvo),
        [...distributionDepartmentIds],
      );
    } else {
      onDistributionChange(
        [...distributionUserIds],
        exigir
          ? [...new Set([...distributionDepartmentIds, alvo])]
          : distributionDepartmentIds.filter((id) => id !== alvo),
      );
    }
  };

  const addRule = () => {
    if (!draftIsValid) return;
    const rule: AccessRuleInput = {
      scope: draft.scope,
      effect: draft.effect,
      userId: draft.scope === AccessRuleScope.USER ? draft.userId : null,
      departmentId: draft.scope === AccessRuleScope.DEPARTMENT ? draft.departmentId : null,
      flags: draft.flags,
    };
    onChange([...rules, rule]);
    if (draftExigeCiencia && podeExigirCiencia(rule)) definirCiencia(rule, true);
    setDraft(emptyDraft());
    setDraftExigeCiencia(false);
  };

  /**
   * Remover a regra tira também a exigência de ciência dela — mas só se nenhuma
   * OUTRA regra remanescente alcançar o mesmo alvo. Sem essa checagem, apagar
   * uma das duas regras do mesmo departamento silenciaria a ciência da que
   * ficou.
   */
  const removeRule = (index: number) => {
    const removida = rules[index];
    const restantes = rules.filter((_, i) => i !== index);
    onChange(restantes);

    if (!removida || !exigeCiencia(removida)) return;
    const alvo = alvoDaRegra(removida);
    if (!alvo) return;
    const aindaAlcancado = restantes.some(
      (rule) => alvoDaRegra(rule) === alvo && podeExigirCiencia(rule) && exigeCiencia(rule),
    );
    if (!aindaAlcancado) definirCiencia(removida, false);
  };

  const toggleRuleFlag = (index: number, flag: DocumentPermissionFlag) => {
    onChange(
      rules.map((rule, i) => {
        if (i !== index) return rule;
        const has = rule.flags.includes(flag);
        const flags = has ? rule.flags.filter((f) => f !== flag) : [...rule.flags, flag];
        // O schema exige ao menos uma flag; desmarcar a última é remover a regra
        // inteira, e isso é decisão explícita do usuário — mantemos a última.
        return { ...rule, flags: flags.length === 0 ? rule.flags : flags };
      }),
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {/* ── Nova regra ──────────────────────────────────────────────────── */}
      <Card>
        <CardHeader
          headingLevel={3}
          title="Adicionar regra de acesso"
          description="Escolha o alcance da regra, se ela permite ou nega, e quais das seis permissões ela cobre."
        />

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Field label="Alcance" required>
            <Select
              value={draft.scope}
              disabled={disabled}
              onValueChange={(scope) =>
                setDraft({ ...draft, scope: scope as AccessRuleScope, userId: null, departmentId: null })
              }
              options={[
                {
                  value: AccessRuleScope.USER,
                  label: ACCESS_SCOPE_LABEL[AccessRuleScope.USER],
                  description: 'Aplica-se a um colaborador específico. Tem a maior precedência.',
                },
                {
                  value: AccessRuleScope.DEPARTMENT,
                  label: ACCESS_SCOPE_LABEL[AccessRuleScope.DEPARTMENT],
                  description: 'Aplica-se a todos os membros do departamento escolhido.',
                },
                {
                  value: AccessRuleScope.ORGANIZATION,
                  label: ACCESS_SCOPE_LABEL[AccessRuleScope.ORGANIZATION],
                  description: 'Aplica-se a todos da organização. Tem a menor precedência.',
                },
              ]}
            />
          </Field>

          {draft.scope === AccessRuleScope.USER ? (
            <Field label="Colaborador" required>
              <Combobox
                items={userItems}
                value={draft.userId}
                disabled={disabled}
                onValueChange={(userId) => setDraft({ ...draft, userId })}
                placeholder="Buscar colaborador…"
                criar={{
                  rotulo: 'Novo colaborador',
                  dialogo: (props) => <CadastroRapidoDeColaborador {...props} />,
                }}
                emptyMessage="Nenhum colaborador encontrado."
              />
            </Field>
          ) : null}

          {draft.scope === AccessRuleScope.DEPARTMENT ? (
            <Field label="Departamento" required>
              <Combobox
                items={departmentItems}
                value={draft.departmentId}
                disabled={disabled}
                onValueChange={(departmentId) => setDraft({ ...draft, departmentId })}
                placeholder="Buscar departamento…"
                criar={{
                  rotulo: 'Novo departamento',
                  dialogo: (props) => <CadastroRapidoDeDepartamento {...props} />,
                }}
                emptyMessage="Nenhum departamento encontrado."
              />
            </Field>
          ) : null}

          <div className="lg:col-span-2">
            <RadioGroup
              legend="Efeito"
              orientation="horizontal"
              disabled={disabled}
              value={draft.effect}
              onValueChange={(effect) => setDraft({ ...draft, effect: effect as AccessRuleEffect })}
              options={[
                {
                  value: AccessRuleEffect.ALLOW,
                  label: ACCESS_EFFECT_LABEL[AccessRuleEffect.ALLOW],
                  description: 'Concede as permissões marcadas.',
                },
                {
                  value: AccessRuleEffect.DENY,
                  label: ACCESS_EFFECT_LABEL[AccessRuleEffect.DENY],
                  description:
                    'Revoga as permissões marcadas. A negação prevalece em caso de empate de alcance.',
                },
              ]}
            />
          </div>

          <fieldset className="m-0 border-0 p-0 lg:col-span-2">
            <legend className="mb-2 text-sm font-medium text-content">
              Permissões
              <span aria-hidden="true" className="ml-0.5 text-danger-content">
                *
              </span>
              <span className="qc-sr-only"> (obrigatório, selecione ao menos uma)</span>
            </legend>
            <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {ALL_DOCUMENT_PERMISSION_FLAGS.map((flag) => (
                <Checkbox
                  key={flag}
                  disabled={disabled}
                  checked={draft.flags.includes(flag)}
                  onCheckedChange={(checked) =>
                    setDraft({
                      ...draft,
                      flags: checked
                        ? [...draft.flags, flag]
                        : draft.flags.filter((f) => f !== flag),
                    })
                  }
                  label={PERMISSION_FLAG_LABEL[flag]}
                  description={PERMISSION_FLAG_DESCRIPTION[flag]}
                />
              ))}
            </div>
          </fieldset>

          {/*
            A CIÊNCIA MORA NA REGRA.
            Fica logo abaixo das permissões porque depende de uma delas: só faz
            sentido para quem esta regra deixa VISUALIZAR.
          */}
          <div className="lg:col-span-2">
            <Checkbox
              disabled={disabled || !podeExigirCiencia(draft)}
              checked={draftExigeCiencia && podeExigirCiencia(draft)}
              onCheckedChange={setDraftExigeCiencia}
              label="Exigir confirmação de leitura"
              description={
                podeExigirCiencia(draft)
                  ? 'Quem esta regra permite visualizar precisa assinar que leu a versão aprovada.'
                  : draft.scope === AccessRuleScope.ORGANIZATION
                    ? 'Indisponível no alcance Organização: a ciência é registrada por colaborador ou por departamento. Use uma regra por departamento.'
                    : 'Disponível apenas em regras que PERMITEM e incluem Visualizar.'
              }
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            variant="primary"
            disabled={disabled || !draftIsValid}
            leadingIcon={<IconPlus size={15} />}
            onClick={addRule}
          >
            Adicionar regra
          </Button>
        </div>
      </Card>

      {/* ── Regras cadastradas ──────────────────────────────────────────── */}
      <Card padding="none">
        <div className="p-4">
          <CardHeader
            headingLevel={3}
            title={`Regras de acesso (${rules.length})`}
            description="A permissão de cada colaborador resulta da combinação destas regras. O alcance mais específico prevalece; em empate, a negação vence."
          />
          {error ? (
            <p role="alert" className="mt-2 flex items-center gap-1.5 text-sm font-medium text-danger-content">
              <IconAlertTriangle size={15} aria-hidden="true" />
              {error}
            </p>
          ) : null}
        </div>

        {rules.length === 0 ? (
          <EmptyState
            variant="compact"
            icon={<IconUsers size={20} />}
            title="Nenhuma regra cadastrada"
            description="Sem ao menos uma regra de acesso, nenhum colaborador enxergará este documento na Lista Mestra. Adicione uma regra acima."
          />
        ) : (
          <div className="overflow-x-auto border-t border-border">
            <table className="w-full border-collapse text-sm">
              <caption className="qc-sr-only">
                Regras de acesso do documento. Cada linha define um alcance, um efeito e as
                permissões cobertas.
              </caption>
              <thead>
                <tr className="border-b border-border bg-surface-sunken">
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted">
                    Alcance
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted">
                    Efeito
                  </th>
                  {ALL_DOCUMENT_PERMISSION_FLAGS.map((flag) => (
                    <th
                      key={flag}
                      scope="col"
                      className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-content-muted"
                    >
                      <Tooltip content={PERMISSION_FLAG_DESCRIPTION[flag]}>
                        <span className="cursor-help underline decoration-dotted underline-offset-2">
                          {PERMISSION_FLAG_LABEL[flag]}
                        </span>
                      </Tooltip>
                    </th>
                  ))}
                  <th
                    scope="col"
                    className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-content-muted"
                  >
                    <Tooltip content="Quem esta regra permite visualizar precisa assinar que leu a versão aprovada.">
                      <span className="cursor-help underline decoration-dotted underline-offset-2">
                        Exige ciência
                      </span>
                    </Tooltip>
                  </th>
                  <th scope="col" className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-content-muted">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule, index) => {
                  const target = describeTarget(rule, users, departments);
                  const isDeny = rule.effect === AccessRuleEffect.DENY;

                  return (
                    <tr
                      key={`${rule.scope}-${rule.userId ?? ''}-${rule.departmentId ?? ''}-${index}`}
                      className={cn(
                        'border-b border-border-subtle last:border-b-0',
                        isDeny && 'bg-danger-subtle/40',
                      )}
                    >
                      <th scope="row" className="px-3 py-2 text-left font-normal">
                        <span className="block text-xs uppercase tracking-wide text-content-subtle">
                          {ACCESS_SCOPE_LABEL[rule.scope as AccessRuleScope]}
                        </span>
                        <span className="block font-medium text-content">{target}</span>
                      </th>

                      <td className="px-3 py-2">
                        <Badge tone={isDeny ? 'danger' : 'success'} srPrefix="Efeito:">
                          {ACCESS_EFFECT_LABEL[rule.effect as AccessRuleEffect]}
                        </Badge>
                      </td>

                      {ALL_DOCUMENT_PERMISSION_FLAGS.map((flag) => (
                        <td key={flag} className="px-2 py-2 text-center">
                          {/* Checkbox REAL, alcançável por Tab. O legado
                              escondia o input e alternava duas imagens. */}
                          <Checkbox
                            disabled={disabled}
                            checked={rule.flags.includes(flag)}
                            onCheckedChange={() => toggleRuleFlag(index, flag)}
                            hideLabel
                            label={`${PERMISSION_FLAG_LABEL[flag]} para ${target}`}
                            className="justify-center"
                          />
                        </td>
                      ))}

                      <td className="px-2 py-2 text-center">
                        <Checkbox
                          disabled={disabled || !podeExigirCiencia(rule)}
                          checked={exigeCiencia(rule)}
                          onCheckedChange={(marcado) => definirCiencia(rule, marcado)}
                          hideLabel
                          label={`Exigir confirmação de leitura de ${target}`}
                          className="justify-center"
                        />
                      </td>

                      <td className="px-3 py-2 text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={disabled}
                          iconLabel={`Remover a regra de ${target}`}
                          onClick={() => removeRule(index)}
                        >
                          <IconTrash size={15} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

interface DraftRule {
  scope: AccessRuleScope;
  effect: AccessRuleEffect;
  userId: string | null;
  departmentId: string | null;
  flags: DocumentPermissionFlag[];
}

function emptyDraft(): DraftRule {
  return {
    scope: AccessRuleScope.DEPARTMENT,
    effect: AccessRuleEffect.ALLOW,
    userId: null,
    departmentId: null,
    // READ é o padrão sensato: quase toda regra começa por "pode ver".
    flags: [DocumentPermissionFlag.READ],
  };
}

function describeTarget(
  rule: AccessRuleInput,
  users: AccessRuleEditorProps['users'],
  departments: AccessRuleEditorProps['departments'],
): string {
  if (rule.scope === AccessRuleScope.USER) {
    return users.find((user) => user.id === rule.userId)?.name ?? 'Colaborador removido';
  }
  if (rule.scope === AccessRuleScope.DEPARTMENT) {
    return (
      departments.find((dept) => dept.id === rule.departmentId)?.name ?? 'Departamento removido'
    );
  }
  return 'Todos os colaboradores';
}
